import { useCallback, useEffect, useRef, useState } from 'react';
import { isBackgroundTaskSnapshot, type BackgroundTaskSnapshot } from '../../shared/backgroundTasks';
import { i18nService } from '../services/i18n';

/** 只在抽屉可见时轮询；查询、停止共用同步锁，旧账号/会话响应不能回写。 */
export function useBackgroundTasks(sessionId: string, open: boolean) {
  const [snapshot, setSnapshot] = useState<BackgroundTaskSnapshot | null>(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const epoch = useRef(0), locked = useRef(false);
  const api = window.electron.cowork.backgroundTasks;
  const refresh = useCallback(async (stopTaskId?: string): Promise<boolean> => {
    if (locked.current) return false;
    const generation = epoch.current; locked.current = true; setBusy(true); setError('');
    try {
      if (!api) throw new Error(i18nService.t('backgroundTasksunsupported'));
      const value = stopTaskId === undefined ? await api.list(sessionId) : await api.stop(sessionId, stopTaskId);
      if (generation !== epoch.current) return false;
      if (!isBackgroundTaskSnapshot(value)) throw new Error(i18nService.t('backgroundTasksFailed'));
      setSnapshot(value); return true;
    } catch (err) {
      if (generation === epoch.current) { setError(err instanceof Error ? err.message : i18nService.t('backgroundTasksFailed')); setSnapshot(old => old ? { ...old, availability: 'disconnected', tasks: old.tasks.map(row => ({ ...row, canStop: false })) } : null); }
      return false;
    } finally { if (generation === epoch.current) { locked.current = false; setBusy(false); } }
  }, [api, sessionId]);
  useEffect(() => {
    const clear = () => { epoch.current++; locked.current = false; setSnapshot(null); setError(''); setBusy(false); };
    clear(); window.addEventListener('auth:identityChanging', clear);
    return () => { epoch.current++; locked.current = false; window.removeEventListener('auth:identityChanging', clear); };
  }, [sessionId]);
  useEffect(() => {
    if (!open) return;
    let disposed = false, timer: ReturnType<typeof setTimeout>;
    /** 慢请求期间不堆积查询，后台标签暂停请求，恢复后重新校验权威状态。 */
    const poll = async () => {
      if (!document.hidden) await refresh();
      if (!disposed) timer = setTimeout(() => void poll(), 3000);
    };
    void poll();
    return () => { disposed = true; clearTimeout(timer); };
  }, [open, refresh]);
  return { snapshot, busy, error, refresh };
}
